import React, { useCallback, useEffect, useMemo } from 'react';
import { computed, runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';

// `Header` is imported before `StepGanttHeader` deliberately: `project.page.tsx` (transitively)
// imports the entity barrel, and priming that load first avoids a circular-import ordering
// hazard where `step-gantt-header.tsx` reaches `Step` directly and — in an isolated module
// graph, such as this file's own unit test — races `shooting-day.ts` extending it before the
// class body has run. Harmless in the app itself, where the barrel is always loaded first.
import { Header } from './project.page';
import { GanttChart } from '../../components/gantt/gantt-chart';
import { StepGanttHeader } from './components/step-gantt-header';
import { CalendarStepBar } from './components/calendar-step-bar';
import { StageHeader } from '../../features/process/components/stage-header';
import { uiStore } from '../../core/stores/ui-store';
import { useCurrentMember } from '../../hooks/use-current-member';
import { toastPool } from '../../features/toasts/models/toast-pool';
import { Toast } from '../../features/toasts/models/toast';
import styles from './project-calendar.module.scss';

import type { Project } from '../../app/entities/project';
import type { Stage } from '../../app/entities/stage';
import type { Step } from '../../app/entities/step';
import type { GanttItem, GanttPatch, Row } from '../../components/gantt/types';

export type CalendarRowData =
  | { kind: 'stage'; stage: Stage; index: number; completed: number; total: number }
  | { kind: 'step'; step: Step; isViewOnly?: boolean };

export const toGanttItems = (stages: Stage[], isViewOnly?: boolean): GanttItem[] =>
  stages
    .slice()
    .sort((a, b) => a.order - b.order)
    .map((stage, index) => ({
      id: stage._id,
      name: stage.name,
      // PlainDate crosses the boundary as-is: no toLocalDate here, and no fromLocalDate on the
      // way back. Removing that round trip is what closes the PRO-1069 class of defect.
      ...(stage.startDate && { startDate: stage.startDate }),
      ...(stage.dueDate && { dueDate: stage.dueDate }),
      data: {
        kind: 'stage' as const,
        stage,
        index,
        completed: stage.completedSteps ?? 0,
        total: stage.totalSteps ?? 0,
      },
      children: (stage.steps ?? []).map((step) => ({
        id: step._id,
        name: step.displayName,
        isDone: step.done,
        ...(step.startDate && { startDate: step.startDate }),
        ...(step.dueDate && { dueDate: step.dueDate }),
        data: { kind: 'step' as const, step, isViewOnly },
      })),
    }));

// Module scope deliberately: a component declared inside `ProjectCalendarRoute`'s render body
// gets a new function identity every render, and `row-header.tsx` keys off that identity as the
// element TYPE, not a prop — so every visible row's header would fully unmount and remount on
// any MobX-observed change (e.g. a teammate's remote edit), closing whatever dropdown a viewer
// had open on an unrelated row. `isViewOnly` can't be a closure over here, so it travels through
// `CalendarRowData` instead, set once per item by `toGanttItems`.
export const RowHeaderRenderer: React.FC<{ row: Row }> = observer(({ row }) => {
  const data = row.item.data as CalendarRowData;

  if (data.kind === 'step') {
    return <StepGanttHeader step={data.step} isViewOnly={data.isViewOnly} />;
  }

  return (
    <StageHeader
      name={data.stage.name}
      suffix={
        <span className={styles['stageNumber']}>
          {(data.index + 1).toString().padStart(2, '0')}
        </span>
      }
      total={data.total}
      completed={data.completed}
      isActive
    />
  );
});

// Module scope for the same reason as `RowHeaderRenderer` above — `group-bar.tsx` renders this
// as an element TYPE, so a fresh identity per render would remount every visible group bar.
// The old chart drew this badge inside the bar via `GanttItem.nameSuffix`
// (`components/gantt-chart/components/parent-strip.tsx:65`); `renderers.groupBarSuffix` is where
// it lives now.
export const GroupBarSuffix: React.FC<{ row: Row }> = observer(({ row }) => {
  const data = row.item.data as CalendarRowData;

  // Steps are leaves and never reach a group bar; the guard is a type narrowing, not a filter.
  if (data.kind !== 'stage') return null;

  return (
    <span className={styles['stageNumber']}>{(data.index + 1).toString().padStart(2, '0')}</span>
  );
});

/**
 * `GanttChart`'s `onCommit` (spec §3.2): apply every patch optimistically, await the persist
 * call, and revert the WHOLE set — not just whichever patch's own call rejected — if any of them
 * fails. Phase (a) only ever sends one patch, but the dependency work will cascade a single drag
 * into several, so the all-or-nothing shape is load-bearing now even though nothing exercises it
 * with more than one patch yet.
 *
 * `PlainDate` moves through untouched: `patch.startDate`/`patch.dueDate` go straight onto
 * `step.startDate`/`step.dueDate` and into `step.update`, with no `toLocalDate`/`fromLocalDate`
 * anywhere in this function. That round trip through a local-midnight `Date` is what PRO-1069 was,
 * and the chart this route replaced did it on every drag — so nothing here should reintroduce a
 * conversion on the way to `step.update`.
 *
 * `useBarDrag`/`useBarKeyboard` clear their preview on both a resolved AND a rejected commit and
 * expose no error channel (Task 17's review: correct separation, since the route is the one that
 * produced the rejection). So this function — not anything upstream — is what turns a failed save
 * into something the user can see, via the same `toastPool`/`Toast` pair `Step`'s own error
 * handling already uses (`app/entities/step.ts`), rather than inventing a second mechanism.
 */
export const commitGanttPatches = async (
  project: Project,
  patches: GanttPatch[],
): Promise<void> => {
  const steps = project.stages.flatMap((stage) => stage.steps ?? []);
  const targets = patches
    .map((patch) => ({ patch, step: steps.find((step) => step._id === patch.id) }))
    .filter((target): target is { patch: GanttPatch; step: Step } => !!target.step);

  // M2. An id that resolves to no `Step` — a step a teammate deleted or moved out from under
  // this drag — used to be a bare `return`: no write, no `step.update`, no toast, and the bar
  // snapping back to its old dates with nothing to explain why. That is the one failure on this
  // path the user could not see, in direct contrast to the rejection handler below, which does
  // toast. Any unresolved id now aborts the WHOLE commit and says so, rather than half-applying a
  // set whose contract (spec §3.2) is that it lands or fails together — the same all-or-nothing
  // rule the `catch` below implements for a rejected call.
  if (patches.length === 0) return;
  if (targets.length !== patches.length) {
    toastPool.insert(
      new Toast('That item no longer exists. Refresh to see the latest dates.', 'error'),
    );

    return;
  }

  // Snapshotted before the optimistic write below touches anything, so a partial-failure revert
  // restores every targeted step to what it held before THIS commit, not to whatever an
  // in-flight, unrelated edit happened to leave behind.
  const previous = targets.map(({ step }) => ({
    step,
    startDate: step.startDate,
    dueDate: step.dueDate,
  }));

  runInAction(() => {
    targets.forEach(({ patch, step }) => {
      if (patch.startDate !== undefined) step.startDate = patch.startDate;
      if (patch.dueDate !== undefined) step.dueDate = patch.dueDate;
    });
  });

  try {
    await Promise.all(
      targets.map(({ patch, step }) =>
        step.update({
          ...(patch.startDate !== undefined && { startDate: patch.startDate }),
          ...(patch.dueDate !== undefined && { dueDate: patch.dueDate }),
        }),
      ),
    );
  } catch {
    runInAction(() => {
      previous.forEach(({ step, startDate, dueDate }) => {
        step.startDate = startDate;
        step.dueDate = dueDate;
      });
    });
    toastPool.insert(new Toast('Could not save the change. Please try again.', 'error'));
  }
};

interface CalendarProps {
  project: Project;
}

export const ProjectCalendarRoute: React.FC<CalendarProps> = observer(({ project }) => {
  useEffect(() => {
    uiStore.currentLocation = 'project.calendar';

    return () => {
      uiStore.currentLocation = '';
    };
  }, []);

  const currentMember = useCurrentMember();
  const isViewOnly = currentMember?.role === 'guest';

  // A MobX `computed`, not a bare `useMemo`: `toGanttItems` reads every stage's and step's
  // observable fields, and `useMemo`'s own dependency check would otherwise decide whether to
  // read them at all — skip a re-run and those fields silently drop out of this render's tracked
  // dependencies, so a later change to them would never trigger a re-render. `computed` tracks
  // fully on every `.get()` regardless, and only produces a new `items` reference when a tracked
  // value actually changed, which is what keeps `useGanttState`'s `seeded` from being recomputed
  // on every unrelated re-render.
  const items = useMemo(
    () => computed(() => toGanttItems(project.stages, isViewOnly)),
    [project, isViewOnly],
  ).get();

  const onCommit = useCallback(
    (patches: GanttPatch[]) => commitGanttPatches(project, patches),
    [project],
  );

  return (
    <div className={styles['container']}>
      <Header>
        <div>Calendar</div>
      </Header>

      {/* The chart owns its own scrolling and needs a definite height to do it — see the rule's
          comment in `project-calendar.module.scss`. */}
      <div className={styles['chartWrapper']}>
        <GanttChart
          items={items}
          onCommit={onCommit}
          config={{ readOnly: isViewOnly }}
          renderers={{
            rowHeader: RowHeaderRenderer,
            bar: CalendarStepBar,
            groupBarSuffix: GroupBarSuffix,
          }}
        />
      </div>
    </div>
  );
});
